import * as d3 from 'd3';
import React, { useRef, useEffect, useState } from 'react';
import { useTheme } from "@/components/ThemeProvider";

// Create a responsive container component for D3 charts
export const D3ResponsiveContainer = ({ 
  children, 
  width = '100%', 
  height = '100%' 
}: { 
  children: React.ReactNode; 
  width?: string | number; 
  height?: string | number;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(entries => {
      if (entries[0]) {
        const { width, height } = entries[0].contentRect;
        setDimensions({ width, height });
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, []);

  const containerStyle = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    position: 'relative' as const,
  };

  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement<any>, { 
        width: dimensions.width || 300, 
        height: dimensions.height || 200 
      });
    }
    return child;
  });

  return (
    <div ref={containerRef} style={containerStyle} className="d3-responsive-container">
      {childrenWithProps}
    </div>
  );
};

export const D3BarChart = ({ 
  data, 
  width = 600, 
  height = 300, 
  color = "#69b3a2",
  valueFormatter = (value: number) => `${value}`,
  margin = { top: 20, right: 30, bottom: 40, left: 40 },
  textColor
}: { 
  data: { name: string; value: number }[]; 
  width?: number; 
  height?: number;
  color?: string;
  valueFormatter?: (value: number) => string;
  margin?: { top: number; right: number; bottom: number; left: number };
  textColor?: string;
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const { theme } = useTheme();
  const isDarkTheme = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  
  // Set text color based on theme or use the provided textColor
  const chartTextColor = textColor || (isDarkTheme ? "#F1F1F1" : "#333333");

  useEffect(() => {
    if (!data || data.length === 0 || !svgRef.current) return;

    // Clear existing chart
    d3.select(svgRef.current).selectAll("*").remove();

    // Set up dimensions
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // Create SVG
    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Create scales
    const x = d3.scaleBand()
      .domain(data.map(d => d.name))
      .range([0, innerWidth])
      .padding(0.1);

    const y = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.value) || 0])
      .range([innerHeight, 0]);

    // Create tooltip
    const tooltip = d3.select("body")
      .append("div")
      .attr("class", "d3-tooltip")
      .style("position", "absolute")
      .style("background", isDarkTheme ? "#1e293b" : "#fff")
      .style("color", chartTextColor)
      .style("border", `1px solid ${isDarkTheme ? "#475569" : "#e2e8f0"}`)
      .style("border-radius", "4px")
      .style("padding", "6px 10px")
      .style("font-size", "12px")
      .style("pointer-events", "none")
      .style("opacity", 0)
      .style("box-shadow", "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)")
      .style("z-index", 100);

    // Draw bars
    svg.selectAll(".bar")
      .data(data)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", d => x(d.name) || 0)
      .attr("y", d => y(d.value))
      .attr("width", x.bandwidth())
      .attr("height", d => innerHeight - y(d.value))
      .attr("fill", color)
      .on("mouseover", function(event, d) {
        d3.select(this).attr("fill", d3.rgb(color).darker(0.2).toString());
        tooltip
          .style("opacity", 0.9)
          .html(`${d.name}: ${valueFormatter(d.value)}`);
      })
      .on("mousemove", function(event) {
        tooltip
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 25) + "px");
      })
      .on("mouseout", function() {
        d3.select(this).attr("fill", color);
        tooltip.style("opacity", 0);
      });

    // Draw x-axis with theme-aware text color
    svg.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x))
      .selectAll("text")
      .attr("transform", "translate(-10,0)rotate(-45)")
      .style("text-anchor", "end")
      .style("fill", chartTextColor);

    // Apply theme-aware styling to all axis elements
    svg.selectAll(".domain, .tick line")
      .style("stroke", chartTextColor);
      
    svg.selectAll(".tick text")
      .style("fill", chartTextColor);

    // Draw y-axis with theme-aware text color
    svg.append("g")
      .call(d3.axisLeft(y))
      .selectAll("text")
      .style("fill", chartTextColor);
      
    // Clean up function to remove tooltip when component unmounts
    return () => {
      d3.select("body").selectAll(".d3-tooltip").remove();
    };

  }, [data, width, height, color, margin, chartTextColor, isDarkTheme, valueFormatter]);

  return (
    <div className="d3-chart-container">
      <svg ref={svgRef} width={width} height={height}></svg>
    </div>
  );
};

export const D3PieChart = ({ 
  data, 
  width = 400, 
  height = 400,
  colors = d3.schemeCategory10,
  showLabels = true,
  donut = false,
  valueFormatter = (value: number) => `${value}`,
  textColor
}: { 
  data: { name: string; value: number }[]; 
  width?: number; 
  height?: number;
  colors?: string[] | readonly string[];
  showLabels?: boolean;
  donut?: boolean;
  valueFormatter?: (value: number) => string;
  textColor?: string;
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const { theme } = useTheme();
  const isDarkTheme = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  
  // Set text color based on theme or use the provided textColor
  const chartTextColor = textColor || (isDarkTheme ? "#F1F1F1" : "#333333");

  useEffect(() => {
    if (!data || data.length === 0 || !svgRef.current) return;

    // Clear existing chart
    d3.select(svgRef.current).selectAll("*").remove();

    const radius = Math.min(width, height) / 2;

    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${width / 2},${height / 2})`);

    const color = d3.scaleOrdinal()
      .domain(data.map(d => d.name))
      .range(colors);

    const pie = d3.pie<typeof data[0]>()
      .value(d => d.value);

    const arcGenerator = d3.arc()
      .innerRadius(donut ? radius * 0.5 : 0)
      .outerRadius(radius * 0.8);

    // Create tooltip
    const tooltip = d3.select("body")
      .append("div")
      .attr("class", "d3-tooltip")
      .style("position", "absolute")
      .style("background", isDarkTheme ? "#1e293b" : "#fff")
      .style("color", chartTextColor)
      .style("border", `1px solid ${isDarkTheme ? "#475569" : "#e2e8f0"}`)
      .style("border-radius", "4px")
      .style("padding", "6px 10px")
      .style("font-size", "12px")
      .style("pointer-events", "none")
      .style("opacity", 0)
      .style("box-shadow", "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)")
      .style("z-index", 100);

    // Create arcs
    svg.selectAll(".arc")
      .data(pie(data))
      .enter()
      .append("path")
      .attr("class", "arc")
      .attr("d", d => arcGenerator(d as any))
      .attr("fill", d => color(d.data.name) as string)
      .attr("stroke", "white")
      .style("stroke-width", "2px")
      .on("mouseover", function(event, d) {
        d3.select(this).attr("opacity", 0.8);
        tooltip
          .style("opacity", 0.9)
          .html(`${d.data.name}: ${valueFormatter(d.data.value)}`);
      })
      .on("mousemove", function(event) {
        tooltip
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 25) + "px");
      })
      .on("mouseout", function() {
        d3.select(this).attr("opacity", 1);
        tooltip.style("opacity", 0);
      });

    // Add labels if enabled with theme-aware text color
    if (showLabels) {
      const labelArc = d3.arc()
        .innerRadius(radius * 0.6)
        .outerRadius(radius * 0.9);

      svg.selectAll(".label")
        .data(pie(data))
        .enter()
        .append("text")
        .attr("transform", d => `translate(${labelArc.centroid(d as any)})`)
        .attr("dy", ".35em")
        .attr("text-anchor", "middle")
        .style("fill", chartTextColor)
        .style("font-size", "11px")
        .style("font-weight", "400")
        .text(d => `${d.data.name}: ${valueFormatter(d.data.value)}`);
    }
    
    // Clean up function to remove tooltip when component unmounts
    return () => {
      d3.select("body").selectAll(".d3-tooltip").remove();
    };

  }, [data, width, height, colors, showLabels, donut, valueFormatter, chartTextColor, isDarkTheme]);

  return (
    <div className="d3-chart-container">
      <svg ref={svgRef} width={width} height={height}></svg>
    </div>
  );
};

export const D3LineChart = ({ 
  data, 
  width = 600, 
  height = 300,
  color = "steelblue",
  valueFormatter = (value: number) => `${value}`,
  margin = { top: 20, right: 30, bottom: 30, left: 50 },
  areaFill = false,
  textColor
}: { 
  data: { name: string; value: number }[]; 
  width?: number; 
  height?: number;
  color?: string;
  valueFormatter?: (value: number) => string;
  margin?: { top: number; right: number; bottom: number; left: number };
  areaFill?: boolean;
  textColor?: string;
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const { theme } = useTheme();
  const isDarkTheme = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  
  // Set text color based on theme or use the provided textColor
  const chartTextColor = textColor || (isDarkTheme ? "#F1F1F1" : "#333333");

  useEffect(() => {
    if (!data || data.length === 0 || !svgRef.current) return;

    // Clear existing chart
    d3.select(svgRef.current).selectAll("*").remove();

    // Set up dimensions
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // Create SVG
    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Create scales
    const x = d3.scalePoint()
      .domain(data.map(d => d.name))
      .range([0, innerWidth])
      .padding(0.5);

    const y = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.value) || 0])
      .range([innerHeight, 0])
      .nice();

    // Create line generator
    const line = d3.line<{ name: string; value: number }>()
      .x(d => x(d.name) || 0)
      .y(d => y(d.value))
      .curve(d3.curveMonotoneX);

    // Create tooltip
    const tooltip = d3.select("body")
      .append("div")
      .attr("class", "d3-tooltip")
      .style("position", "absolute")
      .style("background", isDarkTheme ? "#1e293b" : "#fff")
      .style("color", chartTextColor)
      .style("border", `1px solid ${isDarkTheme ? "#475569" : "#e2e8f0"}`)
      .style("border-radius", "4px")
      .style("padding", "6px 10px")
      .style("font-size", "12px")
      .style("pointer-events", "none")
      .style("opacity", 0)
      .style("box-shadow", "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)")
      .style("z-index", 100);

    // Draw area if enabled
    if (areaFill) {
      const area = d3.area<{ name: string; value: number }>()
        .x(d => x(d.name) || 0)
        .y0(innerHeight)
        .y1(d => y(d.value))
        .curve(d3.curveMonotoneX);

      svg.append("path")
        .datum(data)
        .attr("fill", `${color}30`) // Semi-transparent fill
        .attr("d", area);
    }

    // Draw line
    svg.append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", color)
      .attr("stroke-width", 2)
      .attr("d", line);

    // Add dots with tooltips
    svg.selectAll(".dot")
      .data(data)
      .enter()
      .append("circle")
      .attr("class", "dot")
      .attr("cx", d => x(d.name) || 0)
      .attr("cy", d => y(d.value))
      .attr("r", 4)
      .attr("fill", color)
      .on("mouseover", function(event: any, d: { name: string; value: number }) {
        d3.select(this)
          .attr("r", 6)
          .attr("fill", d3.rgb(color).darker(0.2).toString());
        
        tooltip
          .style("opacity", 0.9)
          .html(`${d.name}: ${valueFormatter(d.value)}`);
      })
      .on("mousemove", function(event: any) {
        tooltip
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 25) + "px");
      })
      .on("mouseout", function() {
        d3.select(this)
          .attr("r", 4)
          .attr("fill", color);
        
        tooltip.style("opacity", 0);
      });

    // Add a transparent overlay for tooltip on line
    svg.append("rect")
      .attr("width", innerWidth)
      .attr("height", innerHeight)
      .style("fill", "none")
      .style("pointer-events", "all")
      .on("mousemove", function(event: any) {
        const [mouseX] = d3.pointer(event);
        const xPos = Math.min(
          innerWidth, 
          Math.max(0, mouseX)
        );
        
        // Find the closest point
        const closestPoint = data.reduce((prev, curr) => {
          const prevX = x(prev.name) || 0;
          const currX = x(curr.name) || 0;
          return Math.abs(xPos - prevX) < Math.abs(xPos - currX) ? prev : curr;
        });
        
        // Highlight the closest dot
        svg.selectAll(".dot")
          .attr("r", (d: { name: string; value: number }) => d.name === closestPoint.name ? 6 : 4)
          .attr("fill", (d: { name: string; value: number }) => d.name === closestPoint.name ? d3.rgb(color).darker(0.2).toString() : color);
        
        // Show tooltip
        tooltip
          .style("opacity", 0.9)
          .html(`${closestPoint.name}: ${valueFormatter(closestPoint.value)}`)
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 25) + "px");
      })
      .on("mouseout", function() {
        svg.selectAll(".dot")
          .attr("r", 4)
          .attr("fill", color);
        
        tooltip.style("opacity", 0);
      });

    // Draw x-axis with theme-aware text color
    svg.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x))
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end")
      .attr("dx", "-.8em")
      .attr("dy", ".15em")
      .style("fill", chartTextColor);

    // Apply theme-aware styling to all axis elements
    svg.selectAll(".domain, .tick line")
      .style("stroke", chartTextColor);
      
    svg.selectAll(".tick text")
      .style("fill", chartTextColor);

    // Draw y-axis with theme-aware text color
    svg.append("g")
      .call(d3.axisLeft(y))
      .selectAll("text")
      .style("fill", chartTextColor);
      
    // Clean up function to remove tooltip when component unmounts
    return () => {
      d3.select("body").selectAll(".d3-tooltip").remove();
    };

  }, [data, width, height, color, margin, areaFill, valueFormatter, chartTextColor, isDarkTheme]);

  return (
    <div className="d3-chart-container">
      <svg ref={svgRef} width={width} height={height}></svg>
    </div>
  );
};

export const D3AreaChart = ({ 
  data, 
  width = 600, 
  height = 300,
  color = "steelblue",
  valueFormatter = (value: number) => `${value}`,
  margin = { top: 20, right: 30, bottom: 30, left: 50 },
  textColor
}: { 
  data: { name: string; value: number }[]; 
  width?: number; 
  height?: number; 
  color?: string;
  valueFormatter?: (value: number) => string;
  margin?: { top: number; right: number; bottom: number; left: number };
  textColor?: string;
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const { theme } = useTheme();
  const isDarkTheme = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  
  // Set text color based on theme or use the provided textColor
  const chartTextColor = textColor || (isDarkTheme ? "#F1F1F1" : "#333333");

  useEffect(() => {
    if (!data || data.length === 0 || !svgRef.current) return;

    // Clear existing chart
    d3.select(svgRef.current).selectAll("*").remove();

    // Set up dimensions
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // Create SVG
    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Create scales
    const x = d3.scalePoint()
      .domain(data.map(d => d.name))
      .range([0, innerWidth])
      .padding(0.5);

    const y = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.value) || 0])
      .range([innerHeight, 0])
      .nice();

    // Create area generator
    const area = d3.area<{ name: string; value: number }>()
      .x(d => x(d.name) || 0)
      .y0(innerHeight)
      .y1(d => y(d.value))
      .curve(d3.curveMonotoneX);

    // Draw area
    svg.append("path")
      .datum(data)
      .attr("fill", `${color}60`)
      .attr("stroke", color)
      .attr("stroke-width", 1.5)
      .attr("d", area);

    // Draw x-axis with theme-aware text color
    svg.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x))
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end")
      .attr("dx", "-.8em")
      .attr("dy", ".15em")
      .style("fill", chartTextColor);

    // Apply theme-aware styling to all axis elements
    svg.selectAll(".domain, .tick line")
      .style("stroke", chartTextColor);
      
    svg.selectAll(".tick text")
      .style("fill", chartTextColor);

    // Draw y-axis with theme-aware text color
    svg.append("g")
      .call(d3.axisLeft(y))
      .selectAll("text")
      .style("fill", chartTextColor);

  }, [data, width, height, color, margin, valueFormatter, chartTextColor]);

  return (
    <div className="d3-chart-container">
      <svg ref={svgRef} width={width} height={height}></svg>
    </div>
  );
};

// Weekly bar chart component
export const D3WeeklyBarChart = ({ 
  data, 
  width = 600, 
  height = 300,
  color = "#3b82f6",
  valueFormatter = (value: number) => `${value}`,
  margin = { top: 20, right: 30, bottom: 50, left: 40 },
  textColor
}: { 
  data: { week: string; count: number }[]; 
  width?: number; 
  height?: number;
  color?: string;
  valueFormatter?: (value: number) => string;
  margin?: { top: number; right: number; bottom: number; left: number };
  textColor?: string;
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const { theme } = useTheme();
  const isDarkTheme = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  
  // Set text color based on theme or use the provided textColor
  const chartTextColor = textColor || (isDarkTheme ? "#F1F1F1" : "#333333");

  useEffect(() => {
    if (!data || data.length === 0 || !svgRef.current) return;

    // Clear existing chart
    d3.select(svgRef.current).selectAll("*").remove();

    // Set up dimensions
    const marginValues = { top: 20, right: 30, bottom: 50, left: 40 };
    const innerWidth = width - marginValues.left - marginValues.right;
    const innerHeight = height - marginValues.top - marginValues.bottom;

    // Create SVG
    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${marginValues.left},${marginValues.top})`);

    // Create scales
    const x = d3.scaleBand()
      .domain(data.map(d => d.week))
      .range([0, innerWidth])
      .padding(0.2);

    const maxValue = d3.max(data, d => d.count) || 0;
    const y = d3.scaleLinear()
      .domain([0, maxValue * 1.1]) // Add 10% padding to top
      .range([innerHeight, 0]);

    // Draw bars
    svg.selectAll(".bar")
      .data(data)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", d => x(d.week) || 0)
      .attr("y", d => y(d.count))
      .attr("width", x.bandwidth())
      .attr("height", d => innerHeight - y(d.count))
      .attr("fill", color) // Blue color
      .on("mouseover", function() {
        d3.select(this).attr("fill", d3.rgb(color).darker().toString());
      })
      .on("mouseout", function() {
        d3.select(this).attr("fill", color);
      });

    // Draw value labels on bars with theme-aware text color
    svg.selectAll(".bar-label")
      .data(data)
      .enter()
      .append("text")
      .attr("class", "bar-label")
      .attr("x", d => (x(d.week) || 0) + x.bandwidth() / 2)
      .attr("y", d => y(d.count) - 5)
      .attr("text-anchor", "middle")
      .attr("font-size", "10px")
      .attr("fill", chartTextColor)
      .text(d => valueFormatter(d.count));

    // Draw x-axis with theme-aware text color
    svg.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x))
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end")
      .attr("dx", "-.8em")
      .attr("dy", ".15em")
      .attr("font-size", "10px")
      .style("fill", chartTextColor);

    // Apply theme-aware styling to all axis elements
    svg.selectAll(".domain, .tick line")
      .style("stroke", chartTextColor);
      
    svg.selectAll(".tick text")
      .style("fill", chartTextColor);

    // Draw y-axis with theme-aware text color
    svg.append("g")
      .call(d3.axisLeft(y).ticks(5))
      .selectAll("text")
      .style("fill", chartTextColor);

    // Add title with theme-aware text color
    svg.append("text")
      .attr("x", innerWidth / 2)
      .attr("y", -marginValues.top / 2)
      .attr("text-anchor", "middle")
      .attr("font-size", "14px")
      .attr("font-weight", "bold")
      .style("fill", chartTextColor)
      .text("Weekly User Registrations");

  }, [data, width, height, color, valueFormatter, chartTextColor]);

  return (
    <div className="d3-chart-container">
      <svg ref={svgRef} width={width} height={height}></svg>
    </div>
  );
};
